import React from "react";
import { ChamberLabel } from "@logora/parliament.parliament_chamber.chamber_label";
import { GroupLabel } from "@logora/parliament.parliament_group.group_label";
import { MemberBox } from "@logora/parliament.parliament_member.member_box";
import {
	CheckIcon,
	CrossIcon,
	ModerateIcon,
} from "@logora/parliament.icons.regular_icons";
import { Label } from "@logora/parliament.ui.label";
import { BooleanField } from "./components/BooleanField";
import sanitizeHtml from "sanitize-html";

export const GOUVERNEMENT_IMAGE_URL =
	"https://tricoteuses-assets.s3.fr-par.scw.cloud/organes/marianne.webp";

export const formatCell = (info) => {
	return info.getValue();
};

export const formatRow = (info) => {
	return info.row.original;
};

export const formatDate = (date) => {
	if (!date) return null;
	return new Date(date).toLocaleDateString();
};

export const formatDateTime = (date) => {
	if (!date) return null;
	return new Date(date).toLocaleString();
};

export const capitalizeFirstLetter = (text) => {
	return text?.charAt(0).toUpperCase() + text?.slice(1) || "";
};

export const formatCapitalize = (value) => {
	return capitalizeFirstLetter(value);
};

export const formatNumber = (value) => {
	return value?.toString() || "0";
};

export const formatNumero = (value) => {
	if (!value) return null;
	return `n°${value?.toString()}`;
};

export const formatNumeroFromUid = (value) => {
	if (!value) return null;

	const slicedStr = value.slice(value.length - 6, value.length);
	return `n°${Number.parseInt(slicedStr).toString()}`;
};

export const formatLabel = (value) => {
	if (!value) return null;

	const colorVariants = {
		success: ["adopté", "Adopté"],
		cancel: ["rejeté", "Rejeté", "Retiré", "Irrecevable", "Irrecevable 40"],
		pending: ["En traitement", "Discuté"],
		neutral: ["Tombé", "A discuter"],
	};

	return (
		<Label
			text={capitalizeFirstLetter(value)}
			variantsObject={colorVariants}
			colorType={value}
		/>
	);
};

export const formatChambre = (value) => {
	if (!value) return null;

	return <ChamberLabel chamber={value} short={true} />;
};

export const formatGroupeParlementaire = (value) => {
	if (!value?.libelle) return null;

	return (
		<GroupLabel
			groupName={value?.libelle}
			color={value?.couleurAssociee || "#000000"}
		/>
	);
};

export const formatBooleen = (value) => {
	return <BooleanField condition={value} />;
};

export const formatAuteurActeur = (value) => {
	if (!value) return null;

	if (value.acteurRef) {
		return (
			<MemberBox
				firstName={value.acteurRef.prenom}
				lastName={value.acteurRef.nom}
				imageUrl={value.acteurRef.urlImage}
				{...(value.acteurRef.groupeParlementaire
					? {
							parliamentGroup: {
								groupName: value.acteurRef.groupeParlementaire?.libelleAbrege,
								color: value.acteurRef.groupeParlementaire?.couleurAssociee,
							},
						}
					: {})}
			/>
		);
	}

	if (
		value.typeAuteur &&
		value.typeAuteur === "Gouvernement" &&
		value.gouvernementOrganeRef
	) {
		return (
			<MemberBox
				firstName={value.gouvernementOrganeRef?.type}
				lastName={value.gouvernementOrganeRef?.libelleAbrege}
				imageUrl={GOUVERNEMENT_IMAGE_URL}
			/>
		);
	}
};

export const formatActeur = (value) => {
	if (!value) return null;

	return (
		<MemberBox
			firstName={value.prenom}
			lastName={value.nom}
			imageUrl={value.urlImage}
			{...(value.groupeParlementaire
				? {
						parliamentGroup: {
							groupName: value.groupeParlementaire?.libelleAbrege,
							color: value.groupeParlementaire?.couleurAssociee,
						},
					}
				: {})}
		/>
	);
};

export const formatActeurSansGroupe = (item) => {
	if (!item) return null;

	return (
		<MemberBox
			firstName={item.prenom}
			lastName={item.nom}
			imageUrl={item.urlImage}
		/>
	);
};

export const formatTexteLegislatif = (value) => {
	if (!value) return null;

	return capitalizeFirstLetter(value?.titrePrincipalCourt);
};

export const formatVotePosition = (value) => {
	if (!value) return null;

	switch (value) {
		case "pour":
			return (
				<div style={{ display: "flex", color: "#2E7D32" }}>
					Pour{" "}
					<CheckIcon
						width={20}
						height={20}
						style={{ marginLeft: ".25em", fill: "#2E7D32" }}
					/>{" "}
				</div>
			);
		case "contre":
			return (
				<div style={{ display: "flex", color: "#D32F2F" }}>
					Contre{" "}
					<CrossIcon
						width={20}
						height={20}
						style={{ marginLeft: ".25em", fill: "#D32F2F" }}
					/>{" "}
				</div>
			);
		case "abstention":
			return (
				<div style={{ display: "flex", color: "#455A64" }}>
					Abstention{" "}
					<ModerateIcon
						width={20}
						height={20}
						style={{ marginLeft: ".25em", fill: "#455A64" }}
					/>{" "}
				</div>
			);
		default:
			return "";
	}
};

export const formatGroupeVotantTotal = (item) => {
	if (!item) return null;
	
	const total =
		Number.parseInt(item.pour) +
		Number.parseInt(item.contre) +
		Number.parseInt(item.abstentions);
	
	return total;
}
export const formatGroupeVotantVote = (item, field) => {
	if (!item) return null;

	const total =
		Number.parseInt(item.pour) +
		Number.parseInt(item.contre) +
		Number.parseInt(item.abstentions);
	const pourPercentage =
		total === 0 ? 0 : Math.floor((item.pour / total) * 100);
	const contrePercentage =
		total === 0 ? 0 : Math.floor((item.contre / total) * 100);
	const abstentionsPercentage =
		total === 0 ? 0 : Math.floor((item.abstentions / total) * 100);
	switch (field) {
		case "pour":
			return (
				<div style={{ display: "flex", color: "#2E7D32" }}>
					<CheckIcon
						width={20}
						height={20}
						style={{ marginRight: ".25em", fill: "#2E7D32" }}
					/>
					{`${pourPercentage}% (${item[field]} ${item[field] === 1 ? "vote" : "votes"})`}{" "}
				</div>
			);
		case "contre":
			return (
				<div style={{ display: "flex", color: "#D32F2F" }}>
					<CrossIcon
						width={20}
						height={20}
						style={{ marginRight: ".25em", fill: "#D32F2F" }}
					/>
					{`${contrePercentage}% (${item[field]} ${item[field] === 1 ? "vote" : "votes"})`}{" "}
				</div>
			);
		case "abstentions":
			return (
				<div style={{ display: "flex", color: "#455A64" }}>
					<ModerateIcon
						width={20}
						height={20}
						style={{ marginRight: ".25em", fill: "#455A64" }}
					/>
					{`${abstentionsPercentage}% (${item[field]} ${item[field] === 1 ? "abstention" : "abstentions"})`}{" "}
				</div>
			);
		default:
			return "";
	}
};

export const formatDateNaisToAge = (field, item) => {
	const currentDate = new Date();
	const birthDate = new Date(item[field]);
	let age = currentDate.getFullYear() - birthDate.getFullYear();
	const monthDiff = currentDate.getMonth() - birthDate.getMonth();

	if (
		monthDiff < 0 ||
		(monthDiff === 0 && currentDate.getDate() < birthDate.getDate())
	) {
		age--;
	}

	return age;
};

export const sanitize = (html) => {
	const sanitizeOptions = {
		allowedTags: ["i", "sup", "br", "strong"],
	};

	let finalHtml = html.replace("<italique>", "<i>");
	finalHtml = finalHtml.replace("</italique>", "</i>");
	const sanitizedHtml = sanitizeHtml(finalHtml, sanitizeOptions);

	return { __html: sanitizedHtml };
};
